import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { DevTool } from '@hookform/devtools';
import { useLocation } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';

import Button from '~/shared/components/ButtonsV2';
import DeleteIcon from '~/shared/assets/icons/delete.svg';
import { Input } from '~/shared/components/Input';
import Typography from '~/shared/components/Typography';
import Tag from '~/shared/components/Tag';

import { SectionFooter } from './SectionFooter';
import { RemoveRoleArnModal } from './RemoveRoleArnModal';
import { SkipRoleArnValidationModal } from './SkipRoleArnValidationModal';
import {
  getRoleArnErrorState,
  getRoleArnInteractionState,
  useRoleArnMutations,
  type RoleArnInfo,
} from '../useRoleArnMutations';
import { useAuthContext } from '~/shared/context/useAuthContext';
import { useOrganizationStatus } from '~/shared/utils/organizationUtils';
import Guard from '~/shared/components/Guard';
import Loader from '~/shared/components/Loader';

export function RoleArn() {
  const { accounts, activeTenantId, activeTenantName } = useAuthContext();
  const { isOrganizationAccount } = useOrganizationStatus(accounts, activeTenantId);
  const [forceEditable, setForceEditable] = useState(false);
  const [showRemoveRoleArnModal, setShowRemoveRoleArnModal] = useState(false);
  const [showSkipRoleArnValidationModal, setShowSkipRoleArnValidationModal] = useState(false);
  const search = useLocation().search;
  const showDevTools = !!new URLSearchParams(search).get('devtools');

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    reset,
    getValues,
    formState: { isLoading, isSubmitting, errors, isValid, isDirty },
    control,
  } = useForm<RoleArnInfo>();

  const {
    roleArnInfo,
    roleArn,
    isRoleArnLoading,
    refetchRoleArn,
    updateRoleArnMutation,
    deleteRoleArnMutation,
  } = useRoleArnMutations({
    accountId: activeTenantId,
    isOrganization: isOrganizationAccount ?? false,
    onUpdateSuccess: () => {
      clearErrors('root');
      refetchRoleArn();
      setForceEditable(false);
      setShowSkipRoleArnValidationModal(false);
    },
    onUpdateError: (error: Error) => {
      if (error.message !== 'Internal server error' && !isOrganizationAccount) {
        setShowSkipRoleArnValidationModal(true);
      }
      setError('root', { message: error.message });
    },
    onDeleteSuccess: () => {
      refetchRoleArn();
      setShowRemoveRoleArnModal(false);
      setForceEditable(false);
    },
  });

  useEffect(() => {
    if (roleArnInfo) {
      reset({ roleArn: roleArnInfo.roleArn });
    }
  }, [reset, roleArnInfo]);

  const onSubmit: SubmitHandler<RoleArnInfo> = (values) => {
    updateRoleArnMutation.mutate(values);
  };

  const {
    hasError: hasRoleArnError,
    fieldErrorId: roleArnFieldErrorId,
    footerErrorId: roleArnFooterErrorId,
    describedBy: roleArnDescribedBy,
  } = getRoleArnErrorState({
    idPrefix: 'role-arn',
    fieldErrorMessage: errors.roleArn?.message,
    hasRootError: Boolean(errors.root),
  });
  const {
    showEditButton,
    showApplyButton,
    isInputDisabled,
    isApplyButtonDisabled,
    isEditButtonDisabled,
    isDeleteButtonDisabled,
  } = getRoleArnInteractionState({
    forceEditable,
    roleArn,
    hasRootError: Boolean(errors.root),
    isFormLoading: isLoading,
    isFormValid: isValid,
    isFormDirty: isDirty,
    isRoleArnLoading,
    isUpdatePending: updateRoleArnMutation.isPending,
    isDeletePending: deleteRoleArnMutation.isPending,
  });

  if (!activeTenantId) {
    return (
      <div className="flex items-center justify-center w-full h-full">
        <Loader variant="xl" />
      </div>
    );
  }

  return (
    <div className="px-[14px]">
      <div className="flex items-center gap-2 py-[30px]">
        <Typography variant="H4-med" fontWeight={500} className="text-primary-900">
          Role&apos;s ARN
        </Typography>
        {activeTenantName ? (
          <Tag
            text={activeTenantName || ''}
            variant="default"
            className="capitalize text-gray-700!"
          />
        ) : (
          <Skeleton
            containerClassName=" block leading-[unset!important]"
            className="h-[28px] leading-[unset!important] rounded-[8px] "
            baseColor="var(--color-gray-200)"
            highlightColor="#FFFF"
            width={80}
          />
        )}
      </div>
      {showDevTools && <DevTool control={control} />}

      <div className="">
        <Typography variant="p3" fontWeight={600} className="text-primary-900 px-0.5 py-1">
          Datafy IAM Role ARN
        </Typography>
        <div className="flex gap-[10px]">
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-1 gap-[10px]">
            <Input
              {...register('roleArn', { required: 'Role ARN is required' })}
              aria-invalid={hasRoleArnError}
              aria-describedby={roleArnDescribedBy}
              disabled={isInputDisabled}
              hasError={hasRoleArnError}
              error={errors.roleArn?.message}
              errorId={roleArnFieldErrorId}
              placeholder="Paste here"
              wrapperClassName="w-full"
            />
            {showApplyButton && (
              <Guard permission="datafy.write.config" mode="disable">
                <div>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={isApplyButtonDisabled}
                    className="w-[80px] disabled:opacity-30"
                    loading={updateRoleArnMutation.isPending}
                  >
                    Apply
                  </Button>
                </div>
              </Guard>
            )}
          </form>
          {showEditButton && (
            <Guard permission="datafy.write.config" mode="disable">
              <Button
                type="button"
                variant="third"
                disabled={isEditButtonDisabled}
                onClick={() => {
                  setForceEditable(true);
                  reset(getValues(), {});
                }}
                className="w-[80px] disabled:opacity-30"
              >
                Edit
              </Button>
            </Guard>
          )}
          {!isOrganizationAccount && (
            <Guard permission="datafy.write.config" mode="disable">
              <button
                type="button"
                className="w-10 h-11 grid place-items-center group cursor-pointer disabled:cursor-not-allowed"
                onClick={() => setShowRemoveRoleArnModal(true)}
                disabled={isDeleteButtonDisabled}
                aria-label="Remove IAM role"
              >
                <DeleteIcon className="w-5 h-5 text-gray-900 group-disabled:text-gray-400" />
              </button>
            </Guard>
          )}
        </div>
      </div>

      <SectionFooter
        infoProps={
          isLoading || isRoleArnLoading || isSubmitting || updateRoleArnMutation.isPending
            ? {
              variant: 'none',
              text: (
                <div className="h-[24px] py-1 flex items-center">
                  <Skeleton
                    containerClassName=" block leading-[unset!important]"
                    className="h-[12px] leading-[unset!important] rounded-[8px] "
                    baseColor="var(--color-gray-200)"
                    highlightColor="#FFFF"
                    width={200}
                  />
                </div>
              ),
            }
            : errors.root
              ? {
                text: <span id={roleArnFooterErrorId}>Input is invalid</span>,
                variant: 'error',
              }
              : forceEditable || roleArn
                ? {
                  text: (
                    <span>Input the ARN of the IAM role that provides Datafy permissions </span>
                  ),
                  variant: 'success',
                }
                : { text: 'Input the ARN of the IAM role that provides Datafy permissions' }
        }
        buttonEl={
          <Button
            type="submit"
            variant="primary"
            disabled={isApplyButtonDisabled}
            className="w-[121px] gap-[10px] disabled:opacity-30"
            loading={updateRoleArnMutation.isPending}
          >
            Confirm
          </Button>
        }
      />

      <RemoveRoleArnModal
        isOpen={showRemoveRoleArnModal}
        onClose={() => setShowRemoveRoleArnModal(false)}
        onRemove={() => deleteRoleArnMutation.mutate()}
        isLoading={deleteRoleArnMutation.isPending}
        accountName={activeTenantName}
      />

      <SkipRoleArnValidationModal
        isOpen={showSkipRoleArnValidationModal}
        onClose={() => setShowSkipRoleArnValidationModal(false)}
        onSkip={() => {
          updateRoleArnMutation.mutate({
            roleArn: getValues().roleArn,
            skipValidation: true,
          });
        }}
        isLoading={updateRoleArnMutation.isPending}
      />
    </div>
  );
}
